import React, { useState, useEffect } from 'react';
import { CreditCard, Smartphone, Banknote, Building, AlertTriangle, Zap, Wrench, MoreHorizontal } from 'lucide-react';

const PAYMENT_TYPES = [
    { value: 'rent', label: 'Rent', icon: Banknote, description: 'Monthly rent payment' },
    { value: 'deposit', label: 'Security Deposit', icon: Building, description: 'Deposit collected upfront' },
    { value: 'late_fee', label: 'Late Fee', icon: AlertTriangle, description: 'Penalty for overdue rent' },
    { value: 'utility', label: 'Utility Bill', icon: Zap, description: 'Water, electricity, garbage etc.' },
    { value: 'maintenance', label: 'Maintenance', icon: Wrench, description: 'Repair/service charges' },
    { value: 'other', label: 'Other', icon: MoreHorizontal, description: 'Any other payment type' },
];

const PaymentForm = ({ onSubmit, isLoading, leases }) => {
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentType, setPaymentType] = useState('rent');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [selectedLeaseId, setSelectedLeaseId] = useState('');
    const [selectedLease, setSelectedLease] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentPeriod, setPaymentPeriod] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');

    // When selectedLeaseId changes, find the lease and set amount
    useEffect(() => {
        if (selectedLeaseId && leases.length > 0) {
            const lease = leases.find(l => l.id === parseInt(selectedLeaseId));
            if (lease) {
                setSelectedLease(lease);
                // Only pre-fill rent amount for rent payment type
                if (paymentType === 'rent') {
                    setAmount(lease.rent_amount);
                }
            }
        } else {
            setSelectedLease(null);
            setAmount('');
        }
    }, [selectedLeaseId, leases]);

    // Reset amount when payment type changes (except for rent)
    useEffect(() => {
        if (paymentType !== 'rent' && selectedLease) {
            setAmount('');
        } else if (paymentType === 'rent' && selectedLease) {
            setAmount(selectedLease.rent_amount);
        }
    }, [paymentType]);

    const getPaymentTypeIcon = (type) => {
        const pt = PAYMENT_TYPES.find(t => t.value === type);
        const Icon = pt?.icon || MoreHorizontal;
        return <Icon className="h-4 w-4" />;
    };

    // Get available months for payment period
    const getAvailableMonths = () => {
        const months = [];
        const today = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            months.push({ value, label });
        }
        return months;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!selectedLease) {
            setError('Please select a lease');
            return;
        }
        
        if (!amount || amount <= 0) {
            setError('Please enter a valid amount');
            return;
        }
        
        if (paymentMethod === 'mpesa_stk' && !phoneNumber) {
            setError('Phone number is required for M-Pesa payment');
            return;
        }
        
        if (!paymentPeriod) {
            setError('Please select payment period');
            return;
        }
        
        // Calculate payment period dates
        const [year, month] = paymentPeriod.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0);
        
        try {
            await onSubmit({
                leaseId: selectedLease.id,
                amount: parseFloat(amount),
                paymentType,          // <-- Now passing the selected payment type
                paymentMethod,
                phoneNumber,
                payment_period_start: startDate.toISOString().split('T')[0],
                payment_period_end: endDate.toISOString().split('T')[0],
                notes
            });
        } catch (err) {
            setError(err.message || 'Payment failed');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}
            
            {/* Lease Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Lease
                </label>
                <select
                    value={selectedLeaseId}
                    onChange={(e) => setSelectedLeaseId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                    required
                >
                    <option value="">Select a lease...</option>
                    {leases.map(lease => (
                        <option key={lease.id} value={lease.id}>
                            {lease.asset_name} - Unit {lease.unit_number || 'Main'} - {lease.first_name} {lease.last_name}
                        </option>
                    ))}
                </select>
                {leases.length === 0 && (
                    <p className="text-sm text-red-500 mt-1">No active leases found. Please create a lease first.</p>
                )}
            </div>

           {/* Amount */}
<div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
        Amount (KES)
    </label>
    <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
        placeholder="25000"
        required
        min="0"
        step="1"
    />
</div>

            {/* Payment Period */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Period
                </label>
                <select
                    value={paymentPeriod}
                    onChange={(e) => setPaymentPeriod(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                    required
                >
                    <option value="">Select month...</option>
                    {getAvailableMonths().map(month => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Payment Type */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                    {PAYMENT_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                            <button
                                key={type.value}
                                type="button"
                                onClick={() => setPaymentType(type.value)}
                                className={`p-2.5 border rounded-lg text-center transition-all ${
                                    paymentType === type.value
                                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                                title={type.description}
                            >
                                <Icon className="h-5 w-5 mx-auto mb-1" />
                                <span className="text-xs font-medium block">{type.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Payment Method */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                </label>
                <div className="grid grid-cols-3 gap-3">
                    <button
                        type="button"
                        onClick={() => setPaymentMethod('cash')}
                        className={`p-3 border rounded-lg flex items-center justify-center space-x-2 ${
                            paymentMethod === 'cash'
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                    >
                        <Banknote className="h-5 w-5" />
                        <span>Cash</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaymentMethod('mpesa_stk')}
                        className={`p-3 border rounded-lg flex items-center justify-center space-x-2 ${
                            paymentMethod === 'mpesa_stk'
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                    >
                        <Smartphone className="h-5 w-5" />
                        <span>M-Pesa STK</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaymentMethod('bank_transfer')}
                        className={`p-3 border rounded-lg flex items-center justify-center space-x-2 ${
                            paymentMethod === 'bank_transfer'
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                    >
                        <CreditCard className="h-5 w-5" />
                        <span>Bank Transfer</span>
                    </button>
                </div>
            </div>
            
            {/* Phone Number for M-Pesa */}
            {paymentMethod === 'mpesa_stk' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        M-Pesa Phone Number
                    </label>
                    <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                        placeholder="254700000000"
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Format: 254XXXXXXXXX (without +). You will receive an STK push on this number.
                    </p>
                </div>
            )}

            {/* Notes */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                    placeholder="Additional notes..."
                />
            </div>
            
            <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
                {isLoading ? 'Processing...' : 'Make Payment'}
            </button>
        </form>
    );
};

export default PaymentForm;